import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import { LoadingScreen } from "./components/common";
import Login from "./pages/Login";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Tenants from "./pages/admin/Tenants";
import UsersPage from "./pages/Users";
import Customers from "./pages/Customers";
import Requesters from "./pages/Requesters";
import Locations from "./pages/Locations";
import Drivers from "./pages/Drivers";
import Assets from "./pages/Assets";
import ProductServiceItems from "./pages/ProductServiceItems";
import SwdDispatch from "./pages/swd/SwdDispatch";
import SwdJobTickets from "./pages/swd/SwdJobTickets";
import SwdTimekeeping from "./pages/swd/SwdTimekeeping";
import SwdMap from "./pages/swd/SwdMap";
import SwdSalesOrders from "./pages/swd/SwdSalesOrders";
import FracDispatch from "./pages/fractank/FracDispatch";
import FracInventory from "./pages/fractank/FracInventory";
import MobileRental from "./pages/fractank/MobileRental";
import MobileDriver from "./pages/fractank/MobileDriver";
import FracMap from "./pages/fractank/FracMap";
import FracSalesOrders from "./pages/fractank/FracSalesOrders";
import MobileMechanic from "./pages/mobile/MobileMechanic";
import FleetChecklists from "./pages/fleet/FleetChecklists";
import FleetPurchasing from "./pages/fleet/FleetPurchasing";
import FleetWorkOrders from "./pages/fleet/FleetWorkOrders";
import FleetInventory from "./pages/fleet/FleetInventory";

import Accounting from "./pages/accounting/Accounting";
import RequestorPortal from "./pages/portal/RequestorPortal";
import ResetPassword from "./pages/ResetPassword";
import RentalStageResponse from "./pages/RentalStageResponse";

const Protected = ({ children, mechanic = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" state={{ from: `${location.pathname}${location.search}` }} replace />;
  if (user.role === "requestor") return <Navigate to="/portal" replace />;
  if (user.role === "mechanic" && !mechanic) return <Navigate to={`/mobilemechanic${location.pathname === "/fleet/work-orders" ? location.search : ""}`} replace />;
  if (mechanic && user.role !== "mechanic") return <Navigate to={`/fleet/work-orders${location.search}`} replace />;
  return children;
};

const ModuleRoute = ({ module, children }) => {
  const { user, tenant, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!tenant?.enabled_modules?.some((enabled) => (Array.isArray(module) ? module : [module]).includes(enabled))) return <Navigate to="/dashboard" replace />;
  return children;
};

const PortalRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "requestor") return <Navigate to="/dashboard" replace />;
  return children;
};

const HomeRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Landing />;
  if (user.role === "requestor") return <Navigate to="/portal" replace />;
  if (user.role === "mechanic") return <Navigate to="/mobilemechanic" replace />;
  if (user.role === "driver") return <Navigate to="/mobiledriver" replace />;
  if (user.role === "salesperson") return <Navigate to="/mobilerental" replace />;
  return <Navigate to={user.role === "platform_admin" ? "/admin/tenants" : "/dashboard"} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/rental-stage-response" element={<RentalStageResponse />} />
          <Route path="/portal" element={<PortalRoute><RequestorPortal /></PortalRoute>} />
          <Route path="/mobilerental" element={<Protected><ModuleRoute module="fractank"><MobileRental /></ModuleRoute></Protected>} />
          <Route path="/mobilemechanic" element={<Protected mechanic><MobileMechanic /></Protected>} />
          <Route path="/mobiledriver" element={<Protected><ModuleRoute module={["fractank", "fleet"]}><MobileDriver /></ModuleRoute></Protected>} />
          <Route path="/" element={<HomeRedirect />} />
          <Route
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/admin/tenants" element={<Tenants />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/customers" element={<Customers />} />
            <Route path="/requesters" element={<Requesters />} />
            <Route path="/locations" element={<Locations />} />
            <Route path="/drivers" element={<Drivers />} />
            <Route path="/assets" element={<Assets />} />
            <Route path="/product-service-items" element={<ProductServiceItems />} />
            <Route path="/swd/dispatch" element={<ModuleRoute module="swd"><SwdDispatch /></ModuleRoute>} />
            <Route path="/swd/tickets" element={<ModuleRoute module="swd"><SwdJobTickets /></ModuleRoute>} />
            <Route path="/swd/time" element={<ModuleRoute module="swd"><SwdTimekeeping /></ModuleRoute>} />
            <Route path="/swd/map" element={<ModuleRoute module="swd"><SwdMap /></ModuleRoute>} />
            <Route path="/swd/orders" element={<ModuleRoute module="swd"><SwdSalesOrders /></ModuleRoute>} />
            <Route path="/fractank/dispatch" element={<ModuleRoute module="fractank"><FracDispatch /></ModuleRoute>} />
            <Route path="/fractank/inventory" element={<ModuleRoute module="fractank"><FracInventory /></ModuleRoute>} />
            <Route path="/fractank/map" element={<ModuleRoute module="fractank"><FracMap /></ModuleRoute>} />
            <Route path="/fractank/orders" element={<ModuleRoute module="fractank"><FracSalesOrders /></ModuleRoute>} />
            <Route path="/fleet/checklists" element={<ModuleRoute module="fleet"><FleetChecklists /></ModuleRoute>} />
            <Route path="/fleet/work-orders" element={<ModuleRoute module="fleet"><FleetWorkOrders /></ModuleRoute>} />
            <Route path="/fleet/purchasing" element={<ModuleRoute module="fleet"><FleetPurchasing /></ModuleRoute>} />
            <Route path="/fleet/inventory" element={<ModuleRoute module="fleet"><FleetInventory /></ModuleRoute>} />
            <Route path="/accounting" element={<ModuleRoute module="accounting"><Accounting /></ModuleRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
